import { type FormEvent, type ReactElement, useId, useState } from 'react';
import { Button, cn, Label } from '@/shared/ui';
import { useAddQuoteNote } from '../model/use-add-quote-note';

export interface QuoteNoteFormProps {
  readonly quoteRequestId: string;
}

const BODY_MAX_LENGTH = 2_000;

/** 내부 메모 폼(견적 설계 §9-6) — 1~2,000자, 고객에게 보이지 않는다. 성공하면 칸을 비운다. */
export const QuoteNoteForm = ({ quoteRequestId }: QuoteNoteFormProps): ReactElement => {
  const [body, setBody] = useState('');
  const bodyId = useId();
  const errorId = `${bodyId}-error`;
  const { addNote, isPending, fieldError, formError } = useAddQuoteNote();
  const message = fieldError ?? formError;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (await addNote({ quoteRequestId, body })) {
      setBody('');
    }
  };

  return (
    <form
      aria-label="메모 작성"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="flex flex-col gap-2"
    >
      <Label htmlFor={bodyId}>내부 메모</Label>
      <textarea
        id={bodyId}
        rows={3}
        maxLength={BODY_MAX_LENGTH}
        value={body}
        disabled={isPending}
        aria-invalid={fieldError ? true : undefined}
        aria-describedby={message ? errorId : undefined}
        className={cn(
          'w-full rounded-2xl border border-input bg-background px-4 py-2 text-sm transition-colors',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          fieldError && 'border-destructive focus-visible:ring-destructive',
        )}
        placeholder="고객에게 보이지 않습니다."
        onChange={(event) => setBody(event.target.value)}
      />
      {message ? (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {message}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="outline"
        size="sm"
        isLoading={isPending}
        className="self-start"
      >
        메모 남기기
      </Button>
    </form>
  );
};
